<template>
  <div
    ref="smsMessagesRef"
    class="flex-col-reverse space-y-4 overflow-y-auto px-4 pb-4"
  >
    <loader :is-loading="isLoading || isFetching" />
    <div
      v-if="hasNextPage && !isFetchingNextPage && !isLoading"
      class="flex w-full items-center justify-center"
    >
      <button class="p-5" @click.stop.prevent="loadNextPage">
        <span class="text-xs text-primary">
          {{ $t("COMMON.LOAD_MORE") }} {{ $t("COMMON.MESSAGE", 2) }}
        </span>
      </button>
    </div>
    <div
      v-for="(conversation, date, dateIndex) in groupedConversation"
      :key="date"
      class="date"
    >
      <span class="text-center">
        <template v-if="isToday(new Date(date))">
          {{ $t("COMMON.DATES.TODAY") }}
        </template>
        <date-time
          v-else
          :date="new Date(date).toISOString()"
          is-fixed
          format="MMMM dd, yyyy"
        />
      </span>
      <thread-item-wrapper
        v-for="(sms, index) in conversation"
        :key="sms.id"
        data-cy="sms-thread-item"
        :disable-comments="
          dateIndex < numberOfDays - 1 || index !== conversation.length - 1
        "
        :comments="log?.data?.comments"
        is-sms
        class="sms-wrapper"
        :class="sms.is_outgoing ? 'outgoing-sms' : 'incoming-sms'"
      >
        <template #content>
          <div class="flex flex-col space-y-2 border-b text-gray-500">
            <div class="space-y-3 px-6 py-4">
              <div class="flex w-full space-x-3">
                <div
                  class="flex-1 whitespace-pre-wrap text-label"
                  data-cy="sms-message"
                >
                  {{ sms.message }}
                </div>
              </div>
              <div v-if="sms.status_message" class="flex space-x-1">
                <icon-base :icon="IconErrorWarning" class="text-error" />
                <span class="text-error">{{ sms.status_message }}</span>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <div v-if="sms" class="flex w-full justify-between space-y-1">
            <div class="flex-row-full-justify-between wrapper">
              <div class="wrap-children flex items-center space-x-2">
                <sms-status v-if="sms.status" :status="sms.status" />
                <avatar-with-name
                  v-if="sms.sender.id"
                  :user="getName(sms.sender.name)"
                  data-cy="sms-sender"
                />
                <span v-if="sms.from">
                  {{ sms.from }}
                </span>
                <icon-base
                  :icon="IconIncomingArrow"
                  height="12"
                  width="12"
                  :class="[
                    sms.is_outgoing
                      ? 'rotate-180 transform text-primary'
                      : 'text-turquoise',
                  ]"
                />
                <span
                  v-if="sms.receiver.id || (sms.to && sms.from)"
                  class="text-xs font-medium"
                >
                  {{ $t("ACTIVITY.SMS.TO") }}
                </span>
                <avatar-with-name
                  v-if="sms.receiver?.id"
                  :user="getName(sms.receiver?.name)"
                  data-cy="sms-receiver"
                />
                <span v-if="sms.to">
                  {{ sms.to }}
                </span>
                <links-clicked-tooltip-icon
                  v-if="!!sms.link_clicked_events?.length"
                  data-cy="sms-links-clicked"
                >
                  <div
                    class="flex flex-col space-y-1"
                    data-cy="sms-links-clicked-detail"
                  >
                    <div
                      v-for="datetime in sms.link_clicked_events"
                      :key="datetime"
                      class="max-w-200 truncate text-xxs text-white/80"
                    >
                      <span class="text-white">
                        {{
                          formatDateCustom(
                            datetime,
                            "MMM dd, yyyy hh:mm a",
                            true
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </links-clicked-tooltip-icon>
                <span v-if="sms?.created_at">&#8226;</span>
                <date-time :date="sms?.created_at" />
              </div>
            </div>
          </div>
        </template>
      </thread-item-wrapper>
    </div>

    <div class="space-y-4">
      <log-quick-submit
        v-model="message"
        class="quick-submit-form"
        :placeholder="$t('ACTIVITY.SMS.MESSAGE_PLACEHOLDER')"
        :return-placeholder="$t('ACTIVITY.SMS.RETURN_PLACEHOLDER')"
        :loading="isSending"
        @submit="sendSms"
      >
        <template #actionBar>
          <sms-from-number :prepend="$t('COMMON.FROM')" placement="top-start" />
        </template>
      </log-quick-submit>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  type Ref,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRef,
  useTemplateRef,
  watch,
} from "vue";

import isToday from "date-fns/isToday";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { storeToRefs } from "pinia";

import { LogEndpointType } from "@/enums/communicationLogs";
import { getAssignedPhoneNumber } from "@/helpers/communication";
import { formatDateCustom } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { SMS_CHANNEL } from "@/helpers/websockets/common";
import { useLocalStorageSetting } from "@/hooks/options";
import {
  useReplyToSmsMutation,
  useSmsConversationQuery,
} from "@/hooks/queries/useCommunication";
import useSmsStore from "@/stores/sms";

import LinksClickedTooltipIcon from "@/components/communicationLogs/communication/emailThread/LinksClickedTooltipIcon.vue";
import SmsFromNumber from "@/components/communicationLogs/communication/sms/SmsFromNumber.vue";
import SmsStatus from "@/components/communicationLogs/communication/sms/SmsStatus.vue";
import ThreadItemWrapper from "@/components/communicationLogs/communication/threads/openState/ThreadItemWrapper.vue";
import LogQuickSubmit from "@/components/communicationLogs/forms/LogQuickSubmit.vue";
import IconErrorWarning from "@/components/icons/IconErrorWarning.vue";
import IconIncomingArrow from "@/components/icons/IconIncomingArrow.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { SmsLog } from "@/models/communicationLogs";

interface Props {
  log: SmsLog;
}

const { log } = defineProps<Props>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Sms,
  logId: log.data.id,
});

const assignedPhoneNumber = useLocalStorageSetting("assignedPhoneNumber", null);
const { incomingSmsEvents } = storeToRefs(useSmsStore());
const message = ref("");
const initialLoadComplete = ref(false);
const smsMessagesRef = useTemplateRef("smsMessagesRef");
const {
  data: conversationData,
  isLoading,
  status,
  isFetching,
  refetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  // we have id check in the parent
} = useSmsConversationQuery(toRef(log.data.sms_conversation_id) as Ref<number>);
const { mutateAsync: replyToSms, isPending: isSending } =
  useReplyToSmsMutation();
const allDataPages = computed(
  () =>
    conversationData.value?.pages?.flatMap(
      (paginatedResponse) => paginatedResponse.data
    ) ?? []
);

const groupedConversation = computed(() => {
  return groupBy(
    orderBy(allDataPages.value, (message) => new Date(message.created_at)),
    (message) => formatDateCustom(message.created_at, "MM/dd/yyyy", true)
  );
});
const numberOfDays = computed(
  () => Object.keys(groupedConversation.value).length
);

const getName = (name: string | null) => {
  const nameParts = name?.split(" ");
  return { first_name: nameParts?.[0] || "", last_name: nameParts?.[1] || "" };
};

const sendSms = async () => {
  // must reply to incoming message only
  const lastIncomingMessageId = allDataPages.value.find(
    (message) => !message.is_outgoing
  )?.id;
  if (!lastIncomingMessageId || !message.value) {
    return;
  }
  const from = getAssignedPhoneNumber(assignedPhoneNumber.value) ?? "";
  await replyToSms({
    id: lastIncomingMessageId,
    message: message.value,
    from,
  });
  message.value = "";
};

const loadNextPage = () => {
  initialLoadComplete.value = true;
  fetchNextPage();
};

const scrollToBottom = () => {
  if (!smsMessagesRef.value) {
    return;
  }
  smsMessagesRef.value.scrollTop = smsMessagesRef.value.scrollHeight;
};

watch(
  status,
  async (newValue) => {
    if (newValue === "success" && !initialLoadComplete.value) {
      await nextTick();
      scrollToBottom();
    }
  },
  { immediate: true }
);

watch(
  incomingSmsEvents,
  async (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }
    const haveNewMessageForThisThread = newValue.some(
      (message) => message.sms_conversation_id === log.data.sms_conversation_id
    );
    if (haveNewMessageForThisThread) {
      initialLoadComplete.value = true;
      await refetch();
    }
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  await nextTick();
  scrollToBottom();
  if (log.data.sms_conversation_id) {
    subscribeToChannelEvents(SMS_CHANNEL);
  }
});

onBeforeUnmount(() => {
  leaveChannel(SMS_CHANNEL);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";
:deep(.quick-submit-form) {
  .action-button-wrapper {
    @apply w-full;
  }
}

.date {
  @apply flex w-full flex-col gap-y-4;
}

.v1 {
  .sms-wrapper {
    @apply w-full;
  }
  .outgoing-sms {
    @apply bg-primary/5;
  }
  .incoming-sms {
    @apply bg-success/5;
  }
}

.sms-wrapper {
  @apply w-3/4;
}
.outgoing-sms {
  @apply ml-auto;
}
.incoming-sms {
  @apply mr-auto;
}
.date:deep() {
  .log-footer {
    @apply p-2;
  }
}
</style>
